import { useTranslation, Trans } from 'react-i18next'
import { Layers, Languages } from 'lucide-react'
import WizardModal, { inputCls, selectCls, labelCls } from './WizardModal'
import ImageCropField, { uploadImage } from './ImageCropField'
import { useWS } from '../contexts/WSContext'
import { ORE_TYPES, PIT_STATUS, PIT_TYPES, equipmentOf } from '../equipment'
import { apiError } from '../lib/api'
import { TEXT_LANGS, cleanTextMap } from '../lib/i18nText'

// Wizard de cadastro de Pit: 1) dados (mina, nome, minério, tipo, estado),
// 2) imagem com recorte, 3) equipamentos do pit (um equipamento só opera em um
// pit da mina). CRUD em /api/pits (tabela própria, espelha a produção).
export default function PitWizard({ open, onClose, editing = null, mineId = null }) {
  const { t } = useTranslation()
  const { devices, mines, categories, reloadAdmin } = useWS()
  const deviceLabel = (d) => equipmentOf(d, categories, mines).name || d.name

  const initial = {
    mineId: editing?.mine_id ? String(editing.mine_id) : (mineId ? String(mineId) : (mines[0] ? String(mines[0].id) : '')),
    name: editing?.name || '',
    names: Object.fromEntries(TEXT_LANGS.map(l => [l, editing?.names?.[l] || ''])),
    ore: editing?.ore_type || editing?.ore || '',
    type: editing?.type || PIT_TYPES[0],
    status: editing?.status || PIT_STATUS[0],
    equipmentIds: Array.isArray(editing?.equipment_ids) ? editing.equipment_ids : [],
    image: editing?.image_id
      ? { existingId: editing.image_id, previewUrl: `/api/images/${editing.image_id}` }
      : null,
  }

  // Aberto já de início só quando o pit tem traduções: um `open` derivado do formulário
  // fecharia o bloco no meio da digitação ao apagar a última.
  const namesOpen = Object.keys(cleanTextMap(editing?.names)).length > 0

  const steps = [
    {
      title: t('admin:mines.pit_wizard.step_data'),
      valid: f => f.mineId !== '' && f.name.trim() !== '' && f.ore !== '',
      render: (f, set) => {
        const mine = mines.find(m => String(m.id) === f.mineId)
        const ores = Array.isArray(mine?.ore_types) && mine.ore_types.length ? mine.ore_types : ORE_TYPES
        return (
          <div className="space-y-4">
            <label className="block"><span className={labelCls}>{t('common:entities.mine')}</span>
              {mines.length === 0 ? (
                <p className="mt-1 text-xs text-status-stop bg-status-stop/10 rounded-md px-3 py-2">
                  <Trans i18nKey="admin:mines.pit_wizard.no_mines" components={[<strong key="0" />]} />
                </p>
              ) : (
                <select value={f.mineId} onChange={e => set({ ...f, mineId: e.target.value, ore: '' })} disabled={!!editing} className={selectCls + (editing ? ' opacity-60' : '')}>
                  <option value="" disabled>{t('admin:select.mine')}</option>
                  {mines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              )}
            </label>
            <label className="block"><span className={labelCls}>{t('admin:mines.pit_wizard.name')}</span>
              <input value={f.name} onChange={e => set({ ...f, name: e.target.value })} placeholder={t('admin:mines.pit_wizard.name_ph')} className={inputCls} autoFocus />
            </label>
            <details open={namesOpen} className="rounded-md border border-slate-200 dark:border-slate-700 px-3 py-2">
              <summary className="cursor-pointer select-none text-xs font-medium text-slate-600 dark:text-slate-300">
                <span className="inline-flex items-center gap-1.5">
                  <Languages size={13} /> {t('admin:mines.pit_wizard.names_title')}
                  <span className="tabular-nums text-slate-400 dark:text-slate-500">{Object.keys(cleanTextMap(f.names)).length}/{TEXT_LANGS.length}</span>
                </span>
              </summary>
              <p className="mt-2 text-[11px] text-slate-400 dark:text-slate-500">{t('admin:mines.pit_wizard.names_hint')}</p>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {TEXT_LANGS.map(l => (
                  <label key={l} className="block"><span className={labelCls}>{l.toUpperCase()}</span>
                    <input value={f.names[l]} onChange={e => set({ ...f, names: { ...f.names, [l]: e.target.value } })} placeholder={f.name} className={inputCls} />
                  </label>
                ))}
              </div>
            </details>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className={labelCls}>{t('admin:mines.pit_wizard.ore')}</span>
                <select value={f.ore} onChange={e => set({ ...f, ore: e.target.value })} className={selectCls}>
                  <option value="" disabled>{t('admin:select.generic')}</option>
                  {ores.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
              <label className="block"><span className={labelCls}>{t('admin:mines.pit_wizard.type')}</span>
                <select value={f.type} onChange={e => set({ ...f, type: e.target.value })} className={selectCls}>
                  {PIT_TYPES.map(pt => <option key={pt} value={pt}>{pt}</option>)}
                </select>
              </label>
            </div>
            <label className="block"><span className={labelCls}>{t('admin:mines.pit_wizard.status')}</span>
              <select value={f.status} onChange={e => set({ ...f, status: e.target.value })} className={selectCls}>
                {PIT_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
        )
      },
    },
    {
      title: t('admin:mines.pit_wizard.step_image'),
      valid: () => true,
      render: (f, set) => (
        <div className="space-y-2">
          <span className={labelCls}>{t('admin:mines.pit_wizard.image_label')}</span>
          <ImageCropField
            value={f.image}
            onChange={img => set({ ...f, image: img })}
            aspect={16 / 9}
            hint={t('admin:mines.pit_wizard.image_hint')}
          />
        </div>
      ),
    },
    {
      title: t('admin:mines.pit_wizard.step_equips'),
      valid: () => true,
      render: (f, set) => {
        const mine = mines.find(m => String(m.id) === f.mineId)
        // um equipamento só pode estar em um pit da mina: bloqueia os já usados em outros pits
        const usedElsewhere = new Set(
          (Array.isArray(mine?.pits) ? mine.pits : [])
            .filter(p => !editing || p.id !== editing.id)
            .flatMap(p => Array.isArray(p.equipment_ids) ? p.equipment_ids : [])
        )
        const toggle = (name) => set({
          ...f,
          equipmentIds: f.equipmentIds.includes(name) ? f.equipmentIds.filter(x => x !== name) : [...f.equipmentIds, name],
        })
        return (
          <div className="space-y-2">
            <span className={labelCls}>{t('admin:mines.pit_wizard.equips_label', { count: f.equipmentIds.length })}</span>
            <p className="text-[11px] text-slate-400 dark:text-slate-500">{t('admin:mines.pit_wizard.equips_help')}</p>
            <div className="max-h-56 overflow-y-auto scrollbar-thin border border-slate-200 dark:border-slate-700 rounded-md p-2 space-y-1 bg-white dark:bg-slate-900">
              {devices.length === 0 && <p className="text-xs text-slate-400 dark:text-slate-500">{t('admin:mines.pit_wizard.no_equips')}</p>}
              {devices.map(d => {
                const checked = f.equipmentIds.includes(d.name)
                const blocked = !checked && usedElsewhere.has(d.name)
                return (
                  <label key={d.name} className={`flex items-center gap-2 text-sm ${blocked ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed' : 'text-slate-600 dark:text-slate-300 cursor-pointer'}`}>
                    <input type="checkbox" checked={checked} disabled={blocked} onChange={() => toggle(d.name)} className="accent-[#017f96]" />
                    <span className="truncate">{deviceLabel(d)}</span>
                    {blocked && <span className="ml-auto text-[10px] text-slate-300 dark:text-slate-600">{t('admin:mines.pit_wizard.in_other_pit')}</span>}
                  </label>
                )
              })}
            </div>
          </div>
        )
      },
    },
  ]

  const finish = async (f) => {
    let imageId = f.image?.existingId ?? null
    if (f.image?.blob) {
      const up = await uploadImage(f.image, 'pit')
      imageId = up.id
    }
    const body = {
      mine_id: Number(f.mineId),
      name: f.name.trim(),
      names: cleanTextMap(f.names),
      ore: f.ore,
      type: f.type,
      status: f.status,
      image_id: imageId,
      equipment_ids: f.equipmentIds,
    }
    const url = editing ? `/api/pits/${editing.id}` : '/api/pits'
    const res = await fetch(url, { method: editing ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
    if (!res.ok) throw await apiError(res)
    reloadAdmin()
  }

  return (
    <WizardModal
      open={open}
      onClose={onClose}
      title={editing ? t('admin:mines.edit_pit') : t('admin:mines.add_pit')}
      icon={<Layers size={18} className="text-brand" />}
      steps={steps}
      initial={initial}
      onFinish={finish}
      finishLabel={editing ? t('common:actions.save') : t('admin:actions.register')}
    />
  )
}
